import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { breadcrumbSchema, breadcrumbsFromPath, organizationSchema } from "@/lib/structuredData";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const url = "https://yootelco.com/privacy-policy";
  const metaTitle = "Privacy Policy | Yootelco";
  const metaDescription =
    "Learn how Yootelco collects, uses, and protects your data, in line with GDPR and international privacy regulations.";

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(breadcrumbsFromPath("/privacy-policy")))}
        </script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-10 pt-[120px] md:pb-12 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container mx-auto max-w-[1080px] text-center">
          <h1 className="mx-auto max-w-[860px] font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
            Privacy Policy
          </h1>
          <p className="mx-auto mt-4 max-w-[620px] text-[15px] font-medium text-muted-foreground">
            Last updated: July 8, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-shell">
        <div className="mx-auto max-w-[840px] text-[15px] leading-[1.75] text-muted-foreground md:text-[16px]">
            <p>
              Yootelco is a UK-based enterprise telecom messaging platform providing A2P SMS, OTP authentication, RCS messaging, voice termination, and SMS API/gateway services to enterprise and fintech clients worldwide. This Privacy Policy explains how we collect, use, and protect information when you visit yootelco.com or use our services.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">1. Information We Collect</h2>
            <p className="mt-4"><strong className="text-foreground">Information you provide directly:</strong></p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Contact details (name, email, phone number, company name) submitted via contact forms, demo requests, or sign-ups</li>
              <li>Business information provided during onboarding or API integration discussions</li>
              <li>Communications you send us (support tickets, emails)</li>
            </ul>
            <p className="mt-4"><strong className="text-foreground">Information collected automatically:</strong></p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Usage data (pages visited, time on site, referral source), collected via Google Analytics (GA4)</li>
              <li>Device and browser information (IP address, browser type, operating system)</li>
              <li>Cookies and similar tracking technologies (see Section 9)</li>
            </ul>
            <p className="mt-4"><strong className="text-foreground">Information from service usage:</strong></p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>If you use Yootelco's messaging or voice APIs, we may process transactional metadata (delivery status, timestamps, routing information) necessary to provide the service. We do not read or store the content of end-user messages beyond what is required for delivery and billing.</li>
            </ul>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">2. How We Use Information</h2>
            <p className="mt-4">We use collected information to:</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Respond to inquiries and provide requested demos or quotes</li>
              <li>Deliver, maintain, and improve our messaging and voice services</li>
              <li>Send service-related communications and, where consented, marketing updates</li>
              <li>Monitor site performance and security through analytics tools</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">3. Legal Basis for Processing (GDPR/UK GDPR)</h2>
            <p className="mt-4">
              Where UK GDPR or EU GDPR applies, we process personal data based on: consent, performance of a contract, legitimate business interests (e.g., improving our services, direct B2B marketing), or compliance with legal obligations.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">4. Data Sharing</h2>
            <p className="mt-4">We do not sell personal data. We may share information with:</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Service providers who support our infrastructure (hosting, analytics, CRM) under confidentiality obligations</li>
              <li>Telecom carriers and messaging partners as required to deliver services</li>
              <li>Authorities, where required by law</li>
            </ul>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">5. International Data Transfers</h2>
            <p className="mt-4">
              Yootelco operates internationally, with offices in the United Kingdom, Hong Kong, and Armenia. As a result, data may be transferred and processed in countries outside your own, including outside the UK/EEA. Where this occurs, we implement appropriate safeguards (such as Standard Contractual Clauses or equivalent mechanisms) to protect data transferred internationally.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">6. Data Retention</h2>
            <p className="mt-4">
              We retain personal data only as long as necessary to fulfill the purposes outlined in this policy, meet contractual obligations, or comply with applicable law.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">7. Your Rights</h2>
            <p className="mt-4">Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li><strong className="text-foreground">Right to access</strong>: request a copy of your data</li>
              <li><strong className="text-foreground">Right to rectification</strong>: correct inaccurate data</li>
              <li><strong className="text-foreground">Right to erasure</strong>: request deletion of your data</li>
              <li><strong className="text-foreground">Right to restrict processing</strong>: limit how we use your data</li>
              <li><strong className="text-foreground">Right to data portability</strong>: receive your data in a portable format</li>
              <li><strong className="text-foreground">Right to object</strong>: object to certain types of processing</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:info@yootelco.com" className="text-primary hover:underline">info@yootelco.com</a>.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">8. Cookies</h2>
            <p className="mt-4">
              Our website uses cookies and similar technologies, including Google Analytics (GA4), to operate the site, analyze traffic, and improve user experience. You will be asked for your consent to non-essential cookies when you first visit the site, and you can manage your preferences at any time through the cookie banner or your browser settings.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">9. Security</h2>
            <p className="mt-4">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">10. Children's Privacy</h2>
            <p className="mt-4">
              Our services are not directed at individuals under the age of 18. We do not knowingly collect personal data from minors.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">11. Changes to This Policy</h2>
            <p className="mt-4">
              We may update this Privacy Policy from time to time. We will notify you of material changes via email or a prominent notice on our website. The updated policy is effective upon posting.
            </p>

            <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">12. Contact Us</h2>
            <p className="mt-4">For questions, concerns, or to exercise your data rights, please contact us:</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li><strong className="text-foreground">Email:</strong> <a href="mailto:info@yootelco.com" className="text-primary hover:underline">info@yootelco.com</a></li>
              <li><strong className="text-foreground">Phone:</strong> <a href="tel:+442070999469" className="text-primary hover:underline">+44 207 0999469</a></li>
              <li><strong className="text-foreground">Data Protection Officer:</strong> contactable at <a href="mailto:info@yootelco.com" className="text-primary hover:underline">info@yootelco.com</a></li>
              <li><strong className="text-foreground">Registered Office:</strong> 103 High Street, Waltham Cross, Hertfordshire EN8 7AN, United Kingdom</li>
              <li><strong className="text-foreground">Other offices:</strong> Unit 406B, Mirror Tower, 61 Mody Road, Tsim Sha Tsui East, Kowloon, Hong Kong; 26/4 Saryan Street, 0002, Yerevan, Armenia</li>
            </ul>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default PrivacyPolicy;